import { CommonModule } from '@angular/common';
import { TuiButton, TuiDialogContext, TuiError, TuiHint, TuiTextfield } from '@taiga-ui/core';
import { TuiInputModule, TuiSelectModule, TuiTextareaModule, TuiTextfieldControllerModule } from '@taiga-ui/legacy';
import { TUI_VALIDATION_ERRORS, TuiDataListWrapper, TuiFieldErrorPipe } from '@taiga-ui/kit';
import { injectContext } from '@taiga-ui/polymorpheus';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { FlagNameToKeyPipe } from '../../pipes/flag-name-to-key/flag-name-to-key.pipe';
import { LabelDropdownChipsComponent } from '../label-dropdown-chips/label-dropdown-chips.component';
import { Flag, FORM_ERRORS, Label } from '@ffx/common';
import { uniqueFlagKeyValidator, uniqueFlagNameValidator } from '../../utils/validators/flag-validator';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, inject, OnInit, signal } from '@angular/core';

@Component({
    selector: 'ffx-flag-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        LabelDropdownChipsComponent,
        ReactiveFormsModule,
        TuiButton,
        TuiDataListWrapper,
        TuiError,
        TuiFieldErrorPipe,
        TuiHint,
        TuiInputModule,
        TuiSelectModule,
        TuiTextareaModule,
        TuiTextfield,
        TuiTextfieldControllerModule
    ],
    providers: [
        FlagNameToKeyPipe,
        {
            provide: TUI_VALIDATION_ERRORS,
            useValue: {
                required: FORM_ERRORS.REQUIRED,
                duplicateFlagKey: FORM_ERRORS.UNIQUE_FLAG_KEY,
                duplicateFlagName: FORM_ERRORS.UNIQUE_FLAG_NAME
            }
        }
    ],
    templateUrl: './flag-dialog.component.html',
    styleUrl: './flag-dialog.component.scss'
})
export class FlagDialogComponent implements OnInit {
    private readonly context =
        injectContext<TuiDialogContext<Flag | null, { isEdit: boolean; flag: Flag; allFlags: Flag[] }>>();
    private builder = inject(FormBuilder);

    constructor(private flagNameToKey: FlagNameToKeyPipe) {
        this.controls.nameControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
            if (value) {
                this.updateKeyControl(value);
            }
        });
    }

    protected flag: Flag = {
        name: '',
        id: '',
        key: '',
        description: '',
        labels: [],
        status: false
    };
    protected isEdit$ = signal(false);

    protected flagForm = this.builder.nonNullable.group({
        nameControl: [
            '',
            [Validators.required, uniqueFlagNameValidator(this.context.data.allFlags, this.context.data.flag?.id)]
        ],
        keyControl: [
            { value: '', disabled: true },
            [Validators.required, uniqueFlagKeyValidator(this.context.data.allFlags, this.context.data.flag?.id)]
        ],
        descriptionControl: [''],
        labelsControl: [[] as Label[]]
    });

    ngOnInit(): void {
        if (this.context.data) {
            this.isEdit$ = signal(this.context.data.isEdit);

            if (this.context.data.flag) {
                this.flag = this.context.data.flag;
                this.flagForm.patchValue({
                    nameControl: this.flag.name,
                    keyControl: this.flag.key,
                    descriptionControl: this.flag.description,
                    labelsControl: this.flag.labels
                });
            }

            if (!this.isEdit$()) {
                this.flagForm.controls.keyControl.enable();
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get controls() {
        return this.flagForm.controls;
    }

    updateKeyControl(nameValue: string): void {
        if (!this.isEdit$()) {
            const transformedKey = this.flagNameToKey.transform(nameValue);
            this.controls.keyControl.setValue(transformedKey);
            this.controls.keyControl.markAsTouched();
        }
    }

    setLabelIcons(labels: Label[]): void {
        this.flagForm.controls.labelsControl.setValue(labels);
    }

    protected getUpdatedFlag(): Flag {
        return {
            name: this.controls.nameControl.value,
            key: this.controls.keyControl.value,
            description: this.controls.descriptionControl.value,
            labels: this.controls.labelsControl.value,
            id: this.context.data?.flag?.id || '',
            status: this.context.data?.flag?.status || false
        };
    }

    protected handleSubmit(): void {
        this.flagForm.markAllAsTouched();
        if (this.flagForm.valid) {
            this.context.completeWith(this.getUpdatedFlag());
        }
    }

    protected dialogDiscard(): void {
        this.context.completeWith(null);
    }
}
